// src/services/affiliateService.js

/**
 * Affiliate Service
 * Quickbid SaaS
 *
 * Thin wrapper over the affiliate endpoints. Two groups with different bases
 * and different guards, kept apart deliberately:
 *
 *   /super-admin/affiliates/*   YOU. Create affiliates, set rates and terms,
 *                               read anyone's referrals, publish agreements.
 *
 *   /affiliates/*               THE AFFILIATE. Their own data only. The server
 *                               resolves which affiliate from the session, never
 *                               from an id in the URL, so there is no affiliate
 *                               id to pass on any of these calls. That absence
 *                               is the security property — do not "improve" it
 *                               by adding one.
 *
 * ----------------------------------------------------------------------------
 * RETURN SHAPES
 * ----------------------------------------------------------------------------
 * Every function returns response.data unchanged, matching subscriptionService.
 * The server sends { success: true, ... } on success and throws an axios error
 * carrying { success: false, message } on failure, so callers use try/catch and
 * read err.response?.data?.message for a displayable string.
 *
 * ----------------------------------------------------------------------------
 * MONEY IS INTEGER CENTS ACROSS THIS BOUNDARY
 * ----------------------------------------------------------------------------
 * Every *_cents field arrives as an integer. The formatters at the bottom are
 * the only place it becomes a string. Do not divide by 100 in a component —
 * that is how one screen ends up disagreeing with another.
 */

import api from './api.js';

// ============================================================================
// SUPER ADMIN — AFFILIATES
// ============================================================================

const ADMIN = '/super-admin/affiliates';

/**
 * List affiliates with rolled-up referral counts.
 *
 * @param {object} [filters] {status, search}
 * @returns {Promise<{success: boolean, affiliates: Array}>}
 */
export const listAffiliates = async (filters = {}) => {
  const response = await api.get(ADMIN, { params: filters });
  return response.data;
};

/**
 * Everything for one affiliate's detail page in a single call: the affiliate,
 * their referrals, their codes, their audit log, and their agreement status.
 *
 * @returns {Promise<{success, affiliate, referrals, codes, events, agreement}>}
 */
export const getAffiliate = async (affiliateId) => {
  const response = await api.get(`${ADMIN}/${affiliateId}`);
  return response.data;
};

/**
 * Create an affiliate from an existing member.
 *
 * Rate, term and minimum payout default from program settings when omitted, and
 * are COPIED onto the affiliate row. Later changes to the program defaults do
 * not move an existing affiliate.
 *
 * @param {object} payload {memberId, displayName, commissionPercent, termMonths,
 *                          minimumPayoutCents, notes}
 */
export const createAffiliate = async (payload) => {
  const response = await api.post(ADMIN, payload);
  return response.data;
};

/**
 * Change an affiliate's DEFAULTS for future referrals.
 *
 * THIS DOES NOT TOUCH EXISTING REFERRALS. Each referral carries a snapshot of
 * the rate and term it was created with, because the agreement promises rate
 * changes are prospective only. To change one existing referral, use
 * extendReferralTerm — and keep the two as visibly separate actions in the UI.
 * Merged into one control, raising the default would silently rewrite every
 * deal already signed.
 *
 * @param {object} changes {commissionPercent, termMonths, minimumPayoutCents,
 *                          displayName, notes}
 */
export const updateAffiliate = async (affiliateId, changes) => {
  const response = await api.patch(`${ADMIN}/${affiliateId}`, changes);
  return response.data;
};

/**
 * Move an affiliate between statuses, including termination.
 *
 * The two termination kinds differ in what happens to money and the UI must
 * make that clear before calling this:
 *
 *   terminated_cause   fraud, self-referral, spam, misrepresentation,
 *                      reselling codes. Unpaid accrued commission is FORFEITED.
 *   terminated_tail    ended without cause, or by non-renewal. Nothing is
 *                      forfeited. No new referrals attach; existing referrals
 *                      run their FULL remaining term.
 *
 * A reason is required for any termination and is stored in the audit log.
 *
 * @param {object} payload {status, terminationKind, reason}
 */
export const setAffiliateStatus = async (affiliateId, payload) => {
  const response = await api.patch(`${ADMIN}/${affiliateId}/status`, payload);
  return response.data;
};

/**
 * Approve a self-service application.
 *
 * Grants nothing on its own — moves 'applied' to 'agreement_pending'. They
 * still have to accept the agreement before anything is theirs, which is why
 * the UI does not confirm this.
 *
 * Their rate and term were set when they APPLIED, not now. Edit the record
 * first if you want something other than the program defaults.
 */
export const approveApplication = async (affiliateId, note = null) => {
  const response = await api.post(`${ADMIN}/${affiliateId}/approve`, { note });
  return response.data;
};

/**
 * Decline a self-service application. A reason is required and is audited.
 *
 * The row is kept, not deleted, so the same person cannot reapply every week.
 * To let them try again, set their status back to 'applied'.
 */
export const declineApplication = async (affiliateId, reason) => {
  const response = await api.post(`${ADMIN}/${affiliateId}/decline`, { reason });
  return response.data;
};

/**
 * Switch an affiliate between earning commission and referring only.
 *
 * NOT RETROACTIVE. Referrals snapshot the rate when they are created, so
 * turning commission ON only affects shops signed up afterwards, and turning
 * it OFF leaves commission already accrued or paid alone.
 *
 * @param {'commission'|'none'} compensationKind
 */
export const setCompensationKind = async (affiliateId, compensationKind, reason = null) => {
  const response = await api.patch(`${ADMIN}/${affiliateId}/compensation`, {
    compensationKind,
    reason,
  });
  return response.data;
};

/**
 * Let an affiliate decide for themselves whether to take commission, or take
 * that choice away.
 *
 * Unlock for someone who genuinely has a choice — a trainer who may not want
 * the money and may not want to connect a bank account. Leave locked for a
 * professor (never paid) and a standard affiliate (nothing to decide).
 */
export const setCompensationLocked = async (affiliateId, locked) => {
  const response = await api.patch(`${ADMIN}/${affiliateId}/compensation-lock`, { locked });
  return response.data;
};

/** Just the count of waiting applications, for a nav badge. */
export const getPendingApplicationCount = async () => {
  const response = await api.get(`${ADMIN}/pending-count`);
  return response.data;
};

export const listAffiliateReferrals = async (affiliateId, includeEnded = true) => {
  const response = await api.get(`${ADMIN}/${affiliateId}/referrals`, {
    params: { includeEnded },
  });
  return response.data;
};

export const listAffiliateCodes = async (affiliateId) => {
  const response = await api.get(`${ADMIN}/${affiliateId}/codes`);
  return response.data;
};

export const listAffiliateEvents = async (affiliateId, limit = 100) => {
  const response = await api.get(`${ADMIN}/${affiliateId}/events`, { params: { limit } });
  return response.data;
};

/**
 * Members who could become affiliates. Excludes anyone who already is one.
 * Returns an empty list below two characters rather than the whole table.
 */
export const searchMembers = async (q) => {
  const response = await api.get(`${ADMIN}/member-search`, { params: { q } });
  return response.data;
};

// ============================================================================
// SUPER ADMIN — REFERRAL ACTIONS
// ============================================================================

/**
 * Extend ONE referral's term. The explicit, audited counterpart to changing an
 * affiliate's default.
 *
 * The new end date is computed from that client's FIRST PAYMENT, not from
 * today: extending to 84 months means 84 months after commission_start_at.
 *
 * A reason is required. Six months from now the audit row is the only thing
 * that explains why this referral differs from the default.
 */
export const extendReferralTerm = async (referralId, termMonths, reason) => {
  const response = await api.patch(`${ADMIN}/referrals/${referralId}/extend`, {
    termMonths,
    reason,
  });
  return response.data;
};

/**
 * Reattach a lapsed referral. The 12-month attribution window has already
 * closed, so this is a deliberate override. Fails if the company has since
 * picked up a new live referral.
 */
export const restoreReferral = async (referralId, reason) => {
  const response = await api.post(`${ADMIN}/referrals/${referralId}/restore`, { reason });
  return response.data;
};

// ============================================================================
// SUPER ADMIN — MONEY
// ============================================================================

/**
 * What is owed, per affiliate.
 *
 * Each row carries `blocked_reason`, which is null when nothing is holding the
 * balance up and otherwise names the one thing that is: below the minimum,
 * Stripe Connect unfinished, tax information missing, or terminated for cause.
 * Show it — a balance sitting unpaid with no explanation generates an email.
 *
 * Affiliates with no commission rows are omitted.
 *
 * @returns {Promise<{success: boolean, owed: Array}>}
 */
export const getOwedSummary = async () => {
  const response = await api.get(`${ADMIN}/owed`);
  return response.data;
};

/**
 * Every commission row for one affiliate — one per paid invoice.
 *
 * The drill-down that answers "why is this figure what it is". Each row records
 * what the customer paid AND the commission basis, which differ on a taxed
 * invoice because commission is not owed on sales tax.
 */
export const listCommissions = async (affiliateId, limit = 200) => {
  const response = await api.get(`${ADMIN}/${affiliateId}/commissions`, { params: { limit } });
  return response.data;
};

// ============================================================================
// SUPER ADMIN — PROGRAM SETTINGS
// ============================================================================

/**
 * Program-wide defaults. What a NEW affiliate is created with and what a NEW
 * referral snapshots. Nothing existing changes when these do.
 */
export const getAffiliateSettings = async () => {
  const response = await api.get(`${ADMIN}/settings`);
  return response.data;
};

export const updateAffiliateSettings = async (settings) => {
  const response = await api.put(`${ADMIN}/settings`, { settings });
  return response.data;
};

// ============================================================================
// SUPER ADMIN — AGREEMENT VERSIONS
// ============================================================================

/**
 * Published agreement versions.
 *
 * TWO TRACKS. 'commission' is the full document; 'none' is the shorter one for
 * referrers who earn nothing — no rate, no payouts, no 1099. An affiliate only
 * ever sees the one matching their own compensation kind.
 *
 * @param {'commission'|'none'|null} [kind] null returns both.
 */
export const listAgreementVersions = async (kind = null) => {
  const response = await api.get(`${ADMIN}/agreement/versions`, {
    params: kind ? { kind } : {},
  });
  return response.data;
};

export const getAgreementVersion = async (versionId) => {
  const response = await api.get(`${ADMIN}/agreement/versions/${versionId}`);
  return response.data;
};

/**
 * Publish a new agreement version. Append-only — existing versions are never
 * edited, because acceptance records point at a version id and their whole
 * value is that the text has not moved since.
 *
 * requiresReacceptance gates the affiliate dashboard until they accept. Use it
 * for anything material, a rate change above all.
 *
 * An effectiveAt in the future schedules the version rather than publishing it
 * immediately, which is how a change gets its notice period.
 *
 * @param {object} payload {versionLabel, bodyMarkdown, requiresReacceptance, effectiveAt}
 */
export const publishAgreementVersion = async (payload) => {
  const response = await api.post(`${ADMIN}/agreement/versions`, payload);
  return response.data;
};

// ============================================================================
// AFFILIATE — OWN DATA ONLY
// ============================================================================
//
// No affiliate id on any of these. The server resolves it from the session.

const MINE = '/affiliates';

/**
 * THE ONE CALL BEHIND THE WHOLE /affiliate PAGE, in every state.
 *
 * Works for a member who is not an affiliate — that is the point. Three shapes,
 * and the page switches on them:
 *
 *   { affiliate: null, canApply: true }
 *       Not in the program. Render the pitch and an Apply button.
 *
 *   { affiliate: { status: 'applied' | 'declined' | 'invited' }, earnings: null }
 *       Early state. Render a short status message. There is nothing to show.
 *
 *   { affiliate, codes, referrals, earnings, agreement }
 *       agreement_pending onward. The full dashboard.
 *
 * One endpoint rather than a status check plus a fetch, so the page cannot
 * render a half-state while a second call is in flight.
 */
export const getMyAffiliateDashboard = async () => {
  const response = await api.get(`${MINE}/me`);
  return response.data;
};

/**
 * Apply to join the program.
 *
 * No affiliate record needed — any logged-in member can call this. It creates
 * an application and grants nothing; a super admin approves, and even approval
 * only opens the agreement step.
 *
 * Pressing Apply twice returns created:false rather than an error, so the UI
 * does not need to guard against a double click.
 *
 * @returns {Promise<{success, created, reason, affiliate}>}
 */
export const applyToBeAffiliate = async () => {
  const response = await api.post(`${MINE}/apply`);
  return response.data;
};

export const getMyReferrals = async (includeEnded = true) => {
  const response = await api.get(`${MINE}/me/referrals`, { params: { includeEnded } });
  return response.data;
};

export const getMyCodes = async () => {
  const response = await api.get(`${MINE}/me/codes`);
  return response.data;
};

export const getMyEarnings = async () => {
  const response = await api.get(`${MINE}/me/earnings`);
  return response.data;
};

/**
 * Start Stripe Connect onboarding.
 *
 * Returns a SHORT-LIVED, SINGLE-USE url. Follow it immediately with
 * window.location.href — do not store it, render it as a link for later, or
 * open it in a tab the user might come back to in ten minutes. Stripe expires
 * these in minutes.
 *
 * Creates the affiliate's Connect account on first call.
 *
 * @returns {Promise<{success: boolean, url: string}>}
 */
export const startConnectOnboarding = async () => {
  const response = await api.post(`${MINE}/me/connect/onboarding-link`);
  return response.data;
};

/**
 * Re-read the Connect account from Stripe and update our copy.
 *
 * Call this when the affiliate returns from onboarding. The account.updated
 * webhook may not have arrived yet — it is a second or two behind — and
 * without this the page would tell someone who just finished that they still
 * need to connect a bank account.
 *
 * Not for general use. Every other read comes from our own database.
 */
export const refreshConnectState = async () => {
  const response = await api.post(`${MINE}/me/connect/refresh`);
  return response.data;
};

/**
 * A link into the affiliate's own Stripe Express dashboard, where they manage
 * bank details, tax information and 1099s. Only available once payouts are
 * enabled.
 */
export const getConnectDashboardLink = async () => {
  const response = await api.get(`${MINE}/me/connect/dashboard-link`);
  return response.data;
};

/**
 * What switching compensation would actually do to this affiliate.
 *
 * Call this BEFORE showing a confirmation. The two numbers that make the
 * decision informed are `accruedCents` — money they keep either way — and
 * `activeReferrals` — clients who will NOT start earning if they switch
 * commission on, because referrals snapshot the rate when they are created.
 *
 * @returns {Promise<{accruedCents, paidCents, activeReferrals, currentKind, locked}>}
 */
export const getCompensationImpact = async () => {
  const response = await api.get(`${MINE}/me/compensation/impact`);
  return response.data;
};

/**
 * The affiliate switches their own commission on or off.
 *
 * Refused with 403 when their account is locked. Only unlocked affiliates see
 * the control at all, so that should not happen — but the server enforces it
 * rather than trusting the UI.
 *
 * @param {'commission'|'none'} compensationKind
 */
export const setMyCompensation = async (compensationKind) => {
  const response = await api.patch(`${MINE}/me/compensation`, { compensationKind });
  return response.data;
};

/**
 * Hide a client from the affiliate's own list.
 *
 * DISPLAY ONLY. Commission, attribution and the term are untouched — an
 * archived client who starts paying again still earns, and the money still
 * appears in the totals. This exists because a professor running a student
 * code for three years needs a way to stop old cohorts burying the current
 * one.
 */
export const archiveReferral = async (referralId) => {
  const response = await api.post(`${MINE}/me/referrals/${referralId}/archive`);
  return response.data;
};

/** Put an archived client back in the list. */
export const unarchiveReferral = async (referralId) => {
  const response = await api.post(`${MINE}/me/referrals/${referralId}/unarchive`);
  return response.data;
};

export const getMyAgreement = async () => {
  const response = await api.get(`${MINE}/me/agreement`);
  return response.data;
};

export const acceptMyAgreement = async (versionId) => {
  const response = await api.post(`${MINE}/me/agreement/accept`, { versionId });
  return response.data;
};

// ============================================================================
// FORMATTERS
// ============================================================================
//
// The only place a *_cents integer becomes a string. Components call these
// rather than dividing by 100, so two screens cannot round differently.

/** 900 -> "$9.00" */
export const formatCents = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${(n / 100).toFixed(2)}`;
};

/** 15 -> "15%". Trims a trailing ".000" from the numeric(6,3) column. */
export const formatPercent = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return '—';
  return `${parseFloat(n.toFixed(3))}%`;
};

/** 60 -> "5 years". 18 -> "18 months". */
export const formatTermMonths = (months) => {
  const n = parseInt(months, 10);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n % 12 === 0) {
    const years = n / 12;
    return years === 1 ? '1 year' : `${years} years`;
  }
  return `${n} months`;
};

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Whole months from now until a date. Negative when past.
 *
 * Approximate by design — this drives "46 months remaining" on a dashboard, not
 * anything that decides money. The commission_ends_at comparison that actually
 * stops accrual happens in SQL on the server.
 */
export const monthsRemaining = (endsAt) => {
  if (!endsAt) return null;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  return (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
};

/** Tailwind classes for a referral status pill. */
export const referralStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 'pending':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    case 'cancelled':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    case 'expired':
      return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    case 'lapsed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
  }
};

/** Tailwind classes for an affiliate status pill. */
export const affiliateStatusColor = (status) => {
  switch (status) {
    case 'applied':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    case 'declined':
      return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 'onboarding':
    case 'agreement_pending':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    case 'invited':
      return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    case 'suspended':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    case 'terminated_cause':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
    case 'terminated_tail':
    case 'resigned':
    case 'bought_out':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
  }
};

/** Human labels. Keep in step with the schema's CHECK constraints. */
/**
 * How an affiliate is compensated. Not a status — an affiliate can be active,
 * suspended or terminated under either kind.
 */
export const COMPENSATION_KIND_LABELS = {
  commission: 'Earns commission',
  none: 'Referral only',
};

export const compensationKindColor = (kind) =>
  kind === 'none'
    ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';

export const AFFILIATE_STATUS_LABELS = {
  applied: 'Applied',
  declined: 'Declined',
  invited: 'Invited',
  agreement_pending: 'Agreement pending',
  onboarding: 'Onboarding',
  active: 'Active',
  suspended: 'Suspended',
  terminated_cause: 'Terminated for cause',
  terminated_tail: 'Ended without cause',
  resigned: 'Resigned',
  bought_out: 'Bought out',
};

export const REFERRAL_STATUS_LABELS = {
  pending: 'Signed up, not yet paying',
  active: 'Active',
  cancelled: 'Cancelled, can return',
  lapsed: 'Lapsed',
  expired: 'Term ended',
};

const affiliateService = {
  // Super admin — affiliates
  listAffiliates,
  getAffiliate,
  createAffiliate,
  updateAffiliate,
  setAffiliateStatus,
  approveApplication,
  declineApplication,
  getPendingApplicationCount,
  setCompensationKind,
  setCompensationLocked,
  listAffiliateReferrals,
  listAffiliateCodes,
  listAffiliateEvents,
  searchMembers,

  // Super admin — referral actions
  extendReferralTerm,
  restoreReferral,

  // Super admin — money
  getOwedSummary,
  listCommissions,

  // Super admin — settings
  getAffiliateSettings,
  updateAffiliateSettings,

  // Super admin — agreements
  listAgreementVersions,
  getAgreementVersion,
  publishAgreementVersion,

  // Affiliate's own data
  getMyAffiliateDashboard,
  applyToBeAffiliate,
  getMyReferrals,
  getMyCodes,
  getMyEarnings,
  startConnectOnboarding,
  refreshConnectState,
  getConnectDashboardLink,
  getCompensationImpact,
  setMyCompensation,
  archiveReferral,
  unarchiveReferral,
  getMyAgreement,
  acceptMyAgreement,

  // Formatters
  formatCents,
  formatPercent,
  formatTermMonths,
  formatDate,
  monthsRemaining,
  referralStatusColor,
  affiliateStatusColor,
  COMPENSATION_KIND_LABELS,
  compensationKindColor,
  AFFILIATE_STATUS_LABELS,
  REFERRAL_STATUS_LABELS,
};

export default affiliateService;